import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogProps,
  Typography,
  Stack,
  Box,
  Divider,
  useMediaQuery,
  Theme,
  Slide,
} from '@mui/material'
import { FiscalNoteFormData } from '@/components/fiscal-note-form/schema'
import { TransitionProps } from '@mui/material/transitions'
import { forwardRef, useMemo } from 'react'
import { formatCurrency, formatCPF, formatCNPJ } from '@/helpers'
import { useQueryClient } from '@tanstack/react-query'
import { readCompanyServiceCodeOptions } from '@/api/accounting-dealer'
import { useCompany } from '@/context/companies'
import { LoadingButton } from '@mui/lab'
import { Municipality } from '@/types'

export type ConfirmationDialogProps = DialogProps & {
  onClose: () => void
  onConfirm: () => void
  fiscalNoteData?: FiscalNoteFormData
  loading?: boolean
}

export function ConfirmationDialog({
  onConfirm,
  fiscalNoteData,
  loading,
  ...props
}: ConfirmationDialogProps) {
  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('md'))

  const queryClient = useQueryClient()

  const { company } = useCompany()

  const serviceCodes = useMemo(
    () =>
      queryClient.getQueryData(
        readCompanyServiceCodeOptions(company.key).queryKey,
      ),
    [company.key, props.open],
  )

  const municipality = useMemo(() => {
    const municipalities = queryClient.getQueryData<Municipality[]>([
      'municipalities',
      fiscalNoteData?.state_uf,
    ])

    return municipalities?.find(
      (municipality) =>
        municipality.ibge_code === fiscalNoteData?.municipality_ibge_code,
    )
  }, [fiscalNoteData?.state_uf])

  if (!fiscalNoteData) {
    return null
  }

  const {
    value,
    service_code_id,
    cnae_id,
    description,
    borrower,
    csll,
    irrf,
    pis,
    cofins,
    state_uf,
  } = fiscalNoteData

  const serviceCode = serviceCodes?.find(
    (serviceCode) => serviceCode.service_code_id === Number(service_code_id),
  )

  const cnae = company.cnae_companies?.find(
    (cnae) => cnae.cnae_id === Number(cnae_id),
  )

  const isNormalAssumedProfit =
    company.tax_regime?.tax_regime.includes('normal_assumed_profit') &&
    borrower.tax_id_type === 'cnpj'

  return (
    <Dialog
      {...props}
      fullScreen={isMobile}
      TransitionComponent={isMobile ? Transition : undefined}
      data-testid="confirmation-dialog"
    >
      <DialogTitle>Confirmar informações</DialogTitle>

      <DialogContent>
        <Stack spacing={1}>
          <Box>
            <Typography variant="h4" color="textSecondary">
              {borrower.name}
            </Typography>
            <Typography>
              {borrower.tax_id_type === 'cpf'
                ? `CPF: ${formatCPF(borrower.tax_id)}`
                : `CNPJ: ${formatCNPJ(borrower.tax_id)}`}
            </Typography>
          </Box>

          <Divider />

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">Valor</Typography>
            <Typography fontSize={22}>
              {formatCurrency(Number(value))}
            </Typography>
          </Stack>

          <Divider />

          <Box>
            <Typography variant="h6">Serviço</Typography>
            <Typography>
              {serviceCode?.code} - {serviceCode?.label}
            </Typography>
          </Box>

          <Divider />

          <Box>
            <Typography variant="h6">CNAE</Typography>
            <Typography>
              {cnae?.cnae?.code} - {cnae?.cnae?.description}
            </Typography>
          </Box>

          <Divider />

          <Box>
            <Typography variant="h6">Descrição</Typography>
            <Typography whiteSpace="pre-line">
              {description}
            </Typography>
          </Box>

          <Divider />

          <Box>
            <Typography variant="h6">Local de prestação de serviço</Typography>
            <Typography>
              {municipality?.name} - {state_uf}
            </Typography>
          </Box>

          <Divider />

          {isNormalAssumedProfit && (
            <Box>
              <Typography variant="h6" mb={0.5}>
                Impostos
              </Typography>

              <Stack direction="row" spacing={2}>
                <Typography>CSLL: {formatCurrency(Number(csll))}</Typography>
                <Typography>IRRF: {formatCurrency(Number(irrf))}</Typography>
                <Typography>PIS: {formatCurrency(Number(pis))}</Typography>
                <Typography>
                  COFINS: {formatCurrency(Number(cofins))}
                </Typography>
              </Stack>
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button variant="text" onClick={props.onClose}>
          Cancelar
        </Button>
        <LoadingButton onClick={onConfirm} loading={loading}>
          Confirmar e emitir
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

const Transition = forwardRef(
  (props: TransitionProps & { children: React.ReactElement }, ref) => (
    <Slide direction="up" ref={ref} {...props} />
  ),
)
